import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Target, 
  Code, 
  Smartphone, 
  CheckCircle, 
  Star, 
  Users, 
  Award,
  Share2,
  FileText,
  Megaphone,
  Video,
  Globe,
  Search,
  MessageSquare,
  TrendingUp,
  Printer,
  Briefcase,
  Settings
} from 'lucide-react';

const Services: React.FC = () => {
  const services = [
    {
      icon: Share2,
      title: 'Social Media Management',
      description: 'Boost your brand visibility with expert social media management services. CDBS handles posts, DMs, reports, and engagement strategies for consistent digital growth.',
      features: [
        'Monthly posting plans',
        'DM & engagement handling',
        'Trend-based strategies',
        'Performance analytics',
        'Full platform management'
      ],
      path: '/services/social-media-management',
      color: 'from-primary-500 to-primary-600',
      bgColor: 'from-primary-50 to-primary-100',
      seoMeta: 'Boost your brand visibility with expert social media management services. CDBS handles posts, DMs, reports, and engagement strategies for consistent digital growth.',
    },
    {
      icon: FileText,
      title: 'Content Management',
      description: 'Enhance audience engagement with creative content management by Color Drop Business Solutions. We craft shareable posts that amplify your brand presence.',
      features: [
        'High-quality creative posts',
        'Brand-aligned visuals',
        'Engagement-focused designs',
        'Consistent content flow',
        'Shareable graphics & reels'
      ],
      path: '/services/content-management',
      color: 'from-accent-500 to-accent-600',
      bgColor: 'from-accent-50 to-accent-100',
      seoMeta: 'Enhance audience engagement with creative content management by Color Drop Business Solutions. We craft shareable posts that amplify your brand presence.',
    },
    {
      icon: Megaphone,
      title: 'Digital Ad Planning',
      description: 'Get high-performing digital ad campaigns from Color Drop Business Solutions. Data-driven strategies designed to maximize engagement, visibility, and conversions.',
      features: [
        'Targeted ad strategies',
        'Creative ad designs',
        'Data-driven optimization',
        'Budget & ROI management',
        'Detailed performance reports'
      ],
      path: '/services/digital-ad-planning',
      color: 'from-primary-600 to-accent-500',
      bgColor: 'from-primary-50 to-accent-50',
      seoMeta: 'Get high-performing digital ad campaigns from Color Drop Business Solutions. Data-driven strategies designed to maximize engagement, visibility, and conversions.',
    },
    {
      icon: Video,
      title: 'Creative Studio, GIFs & Video Content Creation',
      description: 'Boost your brand with professional photo, video, and GIF creation from CDBS. Creative visuals that tell your story and elevate engagement across platforms.',
      features: [
        'Photos, videos & GIFs',
        'Story-driven visuals',
        'Reel & CGI production',
        'High-quality editing',
        'Engagement-boosting content'
      ],
      path: '/services/creative-studio',
      color: 'from-purple-500 to-pink-500',
      bgColor: 'from-purple-50 to-pink-50',
      seoMeta: 'Boost your brand with professional photo, video, and GIF creation from CDBS. Creative visuals that tell your story and elevate engagement across platforms.',
    },
    {
      icon: Globe,
      title: 'Website Development',
      description: 'Professional website development services by Color Drop Business Solutions. Responsive, high-speed, and conversion-focused web design for your business.',
      features: [
        'Responsive designs',
        'Modern UI/UX',
        'Fast & secure build',
        'Custom features',
        'Conversion-focused layout'
      ],
      path: '/services/web-development',
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'from-blue-50 to-cyan-50',
      seoMeta: 'Professional website development services by Color Drop Business Solutions. Responsive, high-speed, and conversion-focused web design for your business.',
    },
    {
      icon: Smartphone,
      title: 'App Development',
      description: 'Get custom Android and iOS app development from CDBS. Scalable, secure, and user-friendly apps designed for business growth.',
      features: [
        'Android & iOS apps',
        'Smooth user experience',
        'Scalable architecture',
        'End-to-end development',
        'High-performance apps'
      ],
      path: '/services/app-development',
      color: 'from-green-500 to-emerald-500',
      bgColor: 'from-green-50 to-emerald-50',
      seoMeta: 'Get custom Android and iOS app development from CDBS. Scalable, secure, and user-friendly apps designed for business growth.',
    },
    {
      icon: Search,
      title: 'Search Engine Optimization (SEO)',
      description: 'Rank higher and attract organic traffic with expert SEO services by Color Drop Business Solutions. Data-driven strategies for lasting search success.',
      features: [
        'Keyword optimization',
        'On-page improvements',
        'Technical SEO fixes',
        'Competitor analysis',
        'Organic traffic growth'
      ],
      path: '/services/seo',
      color: 'from-orange-500 to-red-500',
      bgColor: 'from-orange-50 to-red-50',
      seoMeta: 'Rank higher and attract organic traffic with expert SEO services by Color Drop Business Solutions. Data-driven strategies for lasting search success.',
    },
    {
      icon: MessageSquare,
      title: 'WhatsApp Automation & Bulk Messaging',
      description: 'Automate and scale customer communication with CDBS WhatsApp bulk messaging solutions. Fast, personalized, and impactful digital engagement.',
      features: [
        'Automated workflows',
        'Personalized bulk sends',
        'Instant promotions',
        'High engagement rates',
        'Easy communication setup'
      ],
      path: '/services/whatsapp-automation',
      color: 'from-green-600 to-teal-500',
      bgColor: 'from-green-50 to-teal-50',
      seoMeta: 'Automate and scale customer communication with CDBS WhatsApp bulk messaging solutions. Fast, personalized, and impactful digital engagement.',
    },
    {
      icon: TrendingUp,
      title: 'Lead Generation (Meta & Google Ads)',
      description: 'Drive qualified leads with Meta and Google ad campaigns by Color Drop Business Solutions. High-quality, conversion-focused lead generation strategies.',
      features: [
        'Targeted lead campaigns',
        'Quality inquiry focus',
        'Smart ad optimization',
        'High conversion setup',
        'Measurable results'
      ],
      path: '/services/lead-generation',
      color: 'from-indigo-500 to-purple-500',
      bgColor: 'from-indigo-50 to-purple-50',
      seoMeta: 'Drive qualified leads with Meta and Google ad campaigns by Color Drop Business Solutions. High-quality, conversion-focused lead generation strategies.',
    },
    {
      icon: Printer,
      title: 'Printing & Branding Solutions',
      description: 'Comprehensive printing and branding solutions by CDBS. Premium designs and quality prints that make your brand stand out offline and online.',
      features: [
        'Premium print designs',
        'Business cards & flyers',
        'Packaging & signage',
        'Creative brand identity',
        'High-quality materials'
      ],
      path: '/services/printing-branding',
      color: 'from-amber-500 to-yellow-500',
      bgColor: 'from-amber-50 to-yellow-50',
      seoMeta: 'Comprehensive printing and branding solutions by CDBS. Premium designs and quality prints that make your brand stand out offline and online.',
    },
    {
      icon: Briefcase,
      title: 'Marketing Consulting',
      description: 'Strategic marketing consulting services by Color Drop Business Solutions. Data-backed insights to enhance brand growth and maximize ROI.',
      features: [
        'Strategy planning',
        'Market insights',
        'ROI improvement',
        'Growth opportunities',
        'Action-based roadmap'
      ],
      path: '/services/marketing-consulting',
      color: 'from-rose-500 to-pink-500',
      bgColor: 'from-rose-50 to-pink-50',
      seoMeta: 'Strategic marketing consulting services by Color Drop Business Solutions. Data-backed insights to enhance brand growth and maximize ROI.',
    },
    {
      icon: Settings,
      title: 'IT Solutions',
      description: 'Reliable IT solutions from Color Drop Business Solutions. Software, automation, and system support to streamline your business operations.',
      features: [
        'Software development',
        'System support',
        'Automation tools',
        'IT infrastructure setup',
        'Performance enhancement'
      ],
      path: '/services/it-solutions',
      color: 'from-slate-500 to-gray-500',
      bgColor: 'from-slate-50 to-gray-50',
      seoMeta: 'Reliable IT solutions from Color Drop Business Solutions. Software, automation, and system support to streamline your business operations.',
    },
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery & Strategy',
      description: 'We analyze your brand, goals, and target audience to create a comprehensive digital strategy.',
    },
    {
      step: '02',
      title: 'Design & Planning',
      description: 'Our team designs stunning visuals and creates detailed project plans.',
    },
    {
      step: '03',
      title: 'Development & Build',
      description: 'We bring your vision to life using cutting-edge technology and best practices.',
    },
    {
      step: '04',
      title: 'Launch & Optimize',
      description: 'We launch your project and continuously monitor and optimize for maximum results.',
    },
  ];

  const benefits = [
    {
      icon: Star,
      title: 'Premium Quality',
      description: 'Exceptional craftsmanship and attention to detail in every project.',
    },
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Seasoned professionals with deep expertise in luxury brand marketing.',
    },
    {
      icon: Award,
      title: 'Proven Results',
      description: 'Data-driven strategies that deliver measurable ROI and business growth.',
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-serif font-bold mb-6">
              Our <span className="text-gradient">Services</span>
            </h1>
            <p className="text-xl text-neutral-600 leading-relaxed">
              Comprehensive digital solutions for your business. From social media management to IT solutions, we provide end-to-end services to elevate your brand and drive growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className="group"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full border border-neutral-100 group-hover:border-primary-200">
                  {/* Icon */}
                  <div className={`w-16 h-16 bg-gradient-to-r ${service.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <service.icon className="w-8 h-8 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-serif font-semibold mb-4 group-hover:text-primary-600 transition-colors duration-300">
                    {service.title}
                  </h3>
                  {/* <p className="text-neutral-600 mb-6 leading-relaxed">
                    {service.description}
                  </p> */}

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-neutral-600">
                        <CheckCircle className="w-4 h-4 text-primary-500 mr-3 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  {/* <Link
                    to={service.path}
                    className={`inline-flex items-center justify-center w-full mt-6 px-6 py-3 rounded-lg font-medium bg-gradient-to-r ${service.color} text-white hover:opacity-90 transition-opacity duration-300 shadow-md hover:shadow-lg`}
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link> */}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-neutral-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Our Process</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              We follow a proven methodology that ensures every project delivers exceptional results while maintaining the highest standards of quality.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold mb-4">{step.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Why Choose Us</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              We combine industry expertise with brand understanding to deliver exceptional results.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className="text-center"
              >
                <div className="w-20 h-20 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4">{benefit.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-24 bg-gradient-to-r from-primary-500 to-accent-500">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
          >
            <h2 className="text-4xl font-serif font-bold text-white mb-6">
              Ready to Elevate Your Brand?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let's discuss how we can help transform your digital presence with our premium services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-accent-50 transition-colors duration-300 inline-flex items-center justify-center">
                Get Free Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/offers" className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/30 transition-colors duration-300 inline-flex items-center justify-center">
                View Our Offers
              </Link>
            </div>
          </motion.div>
        </div>
      </section> */}
    </div>
  );
};

export default Services;
